import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Smartphone, 
  Shield, 
  CreditCard, 
  Gift, 
  Store, 
  Phone,
  ArrowRight,
  CheckCircle,
  Star,
  Zap,
  Globe,
  Lock
} from 'lucide-react'

const Products: React.FC = () => {
  const navigate = useNavigate()
  
  const products = [
    {
      id: 'coretrust',
      name: 'CoreTrust手机',
      description: '基于Web3深度集成的智能手机，提供完整的Web3生态系统解决方案',
      icon: Smartphone,
      color: 'from-blue-500 to-cyan-500',
      features: ['6.7英寸OLED屏幕', '骁龙8 Gen 3处理器', '12GB+512GB存储', '5000mAh电池'],
      price: '¥8,999起'
    },
    {
      id: 'ads',
      name: '熄屏广告系统',
      description: '高颗粒度广告投放和管控，精确到单个设备具体时间段',
      icon: Shield,
      color: 'from-purple-500 to-pink-500',
      features: ['精准投放', '时间控制', '用户画像', '收益分成'],
      price: '免费'
    },
    {
      id: 'airdrop',
      name: '空投助手',
      description: '官方优选Web3优质项目代币空投活动',
      icon: Gift,
      color: 'from-green-500 to-emerald-500',
      features: ['项目筛选', '一键参与', '风险评级', '收益预测'],
      price: '免费'
    },
    {
      id: 'bank',
      name: '数字银行',
      description: '打通数字货币与法币通道，完成数字货币的无感消费',
      icon: CreditCard,
      color: 'from-orange-500 to-red-500',
      features: ['实时汇率', '多币种支持', '无感支付', '安全交易'],
      price: '0.1%手续费'
    },
    {
      id: 'wallet',
      name: '硬件钱包',
      description: '芯片级数据加密存储安全的多链钱包，支持完全离线模式',
      icon: Lock,
      color: 'from-indigo-500 to-purple-500',
      features: ['芯片级加密', '离线模式', '多链支持', '硬件隔离'],
      price: '免费'
    },
    {
      id: 'market',
      name: '应用市场',
      description: '打通区块链应用下载通道，垂直领域应用管理',
      icon: Store,
      color: 'from-teal-500 to-blue-500',
      features: ['去中心化', '智能合约验证', '垂直分类', '安全下载'],
      price: '免费'
    },
    {
      id: 'call',
      name: '去中心化通话',
      description: '基于钱包地址拨打作为通话目标实现全球通话',
      icon: Phone,
      color: 'from-rose-500 to-pink-500',
      features: ['钱包地址通话', '全球网络', '加密通话', '去中心化'],
      price: '按分钟计费'
    }
  ]

  const specifications = [
    { category: '显示', items: ['6.7英寸OLED屏幕', '2K分辨率', '120Hz刷新率', 'HDR10+支持'] },
    { category: '性能', items: ['骁龙8 Gen 3', '12GB LPDDR5X', '512GB UFS 4.0', 'Adreno 750 GPU'] },
    { category: '相机', items: ['5000万主摄', '1200万超广角', '1000万长焦', '3200万前置'] },
    { category: '电池', items: ['5000mAh电池', '120W快充', '50W无线充电', '反向充电'] },
    { category: '连接', items: ['5G网络', 'WiFi 7', '蓝牙5.3', 'NFC支持'] },
    { category: '安全', items: ['硬件级加密', '生物识别', '安全芯片', '隐私保护'] }
  ]

  return (
    <>
      <Helmet>
        <title>产品中心 - CoreTrust手机 | DeepLink Limited</title>
        <meta name="description" content="CoreTrust手机产品介绍，包含熄屏广告、空投助手、数字银行、硬件钱包、应用市场、去中心化通话等核心功能。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                产品中心
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
                探索CoreTrust手机的强大功能，体验Web3技术的无限可能
              </p>
            </motion.div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                核心功能
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                每个功能都经过精心设计，为用户提供最佳的Web3体验
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product, index) => {
                const Icon = product.icon
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="group bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${product.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                      {product.description}
                    </p>
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        主要特性：
                      </h4>
                      <ul className="space-y-2">
                        {product.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {product.price}
                      </span>
                      <Link
                        to={`/products/${product.id}`}
                        className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                      >
                        了解更多
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Specifications */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                技术规格
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                CoreTrust手机采用最先进的技术，为用户提供卓越的性能体验
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {specifications.map((spec, index) => (
                <motion.div
                  key={spec.category}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6"
                >
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    {spec.category}
                  </h3>
                  <ul className="space-y-2">
                    {spec.items.map((item, idx) => (
                      <li key={idx} className="flex items-center text-gray-600 dark:text-gray-400">
                        <Star className="w-4 h-4 text-blue-500 mr-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                准备体验CoreTrust手机？
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
                立即预订，成为Web3时代的先行者
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors duration-300">
                  立即预订
                </button>
                <Link
                  to="/contact"
                  className="bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-colors duration-300"
                >
                  联系我们
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Products